/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import theme from '@salesforce/retail-react-app/app/components/shared/theme'

/**
 * @param {Object} breakpoints
 * @return {string[]} Breakpoint labels ordered from smallest. For example: ['base', 'sm', 'md', 'lg', 'xl', '2xl']
 */
const getBreakpointLabels = (breakpoints) =>
    Object.entries(breakpoints)
        .sort((a, b) => parseFloat(a[1]) - parseFloat(b[1]))
        .map(([key]) => key)

const {breakpoints: defaultBreakpoints} = theme
let themeBreakpoints = defaultBreakpoints
let breakpointLabels = getBreakpointLabels(themeBreakpoints)

/**
 * @param {Object} props
 * @param {string} props.src - Dynamic src having an optional param that can vary with widths. For example: `image[_{width}].jpg` or `image.jpg[?sw={width}&q=60]`
 * @param {(number[]|string[]|Object)} [props.widths] - Image widths relative to the breakpoints, whose units can either be px or vw or unit-less. They will be mapped to the corresponding `sizes` and `srcSet`.
 * @param {Object} [props.breakpoints] - The current theme's breakpoints. If not given, Chakra's default breakpoints will be used.
 * @return {Object} src, sizes, and srcSet props for your image component
 */
export const getResponsiveImageAttributes = ({
    src,
    widths,
    isMobileImg,
    breakpoints = defaultBreakpoints
}) => {
    if (!widths) {
        return {
            src: getSrcWithoutOptionalParams(src)
        }
    }

    themeBreakpoints = breakpoints
    breakpointLabels = getBreakpointLabels(themeBreakpoints)

    return {
        src: getSrcWithoutOptionalParams(src),
        sizes: mapWidthsToSizes(widths),
        srcSet: mapWidthsToSrcSet(widths, src),
        media: isMobileImg ? `(max-width: ${themeBreakpoints['md']})` : null
    }
}

/**
 * @param {(number[]|string[]|Object)} widths
 * @return {string}
 */
const mapWidthsToSizes = (widths) => {
    const _widths = withUnit(Array.isArray(widths) ? widths : widthsAsArray(widths))

    return breakpointLabels
        .slice(0, _widths.length)
        .map((bp, i) => {
            return i === 0 ? _widths[i] : `(min-width: ${themeBreakpoints[bp]}) ${_widths[i]}`
        })
        .reverse()
        .join(', ')
}

/**
 * @param {(number[]|string[]|Object)} widths
 * @return {string}
 */
const mapWidthsToSrcSet = (widths, dynamicSrc) => {
    return widths
        ?.map((width) => {
            const numericWidth = width?.replace('w', '')
            return encodeURI(getSrc(dynamicSrc, numericWidth)) + ` ${width}`
        })
        ?.join(', ')
}

/**
 * @param {(number[]|string[])} widths
 */
const withUnit = (widths) =>
    // By default, unitless value is interpreted as px
    widths.map((width) => (typeof width === 'number' ? `${width}px` : width))

/**
 * @param {Object} widths
 * @example
 * // returns the array [10, 10, 10, 50]
 * widthsAsArray({base: 10, lg: 50})
 */
const widthsAsArray = (widths) => {
    const biggestBreakpoint = breakpointLabels.filter((bp) => Boolean(widths[bp])).pop()

    let mostRecent
    return breakpointLabels.slice(0, breakpointLabels.indexOf(biggestBreakpoint) + 1).map((bp) => {
        if (widths[bp]) {
            mostRecent = widths[bp]
            return widths[bp]
        } else {
            return mostRecent
        }
    })
}
/**
 * @param {string} dynamicSrc
 * @param {number} imageWidth
 * @return {string} Image url having the given width
 * @example
 * // returns https://res.cloudinary.com/demo/image/upload/w_720/image.jpg
 * getSrc('https://res.cloudinary.com/demo/image/upload/image.jpg', 720)
 */
export const getSrc = (dynamicSrc, imageWidth) => {
    // Only unsigned Cloudinary image delivery URLs can safely gain transforms.
    // Keep public IDs, extensions and query parameters intact (including "jpg").
    const cloudinary = dynamicSrc?.match(
        /^(https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)([^?#]*)(.*)$/
    )
    if (!cloudinary || cloudinary[2].startsWith('s--')) return dynamicSrc

    const [, base, path, suffix] = cloudinary
    const segments = path.split('/')
    for (let i = 0; i < segments.length - 1; i++) {
        // Transformations precede the optional version and the public ID.
        if (!/^[a-z]{1,3}_[^/]+(?:,[a-z]{1,3}_[^/]+)*$/.test(segments[i])) break
        if (/(^|,)w_\d+(?=,|$)/.test(segments[i])) {
            segments[i] = segments[i].replace(/(^|,)w_\d+(?=,|$)/, `$1w_${imageWidth}`)
            return base + segments.join('/') + suffix
        }
    }
    return `${base}w_${imageWidth}/${path}${suffix}`
}

/**
 * @param {string} dynamicSrc
 * @example
 * // Returns 'https://example.com/image.jpg'
 * getSrcWithoutOptionalParams('https://example.com/image.jpg[?sw={width}]')
 */
const getSrcWithoutOptionalParams = (dynamicSrc) => {
    const optionalParams = /\[[^\]]+\]/g
    return dynamicSrc?.replace(optionalParams, '')
}
